import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';

const navClass = ({ isActive }) => `site-nav-link ${isActive ? 'active' : ''}`;

function getDisplayName(user, fallback) {
  if (!user) return fallback;
  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return fullName || user.name || user.email || fallback;
}

function getDisplayInitial(user, fallback = 'U') {
  const displayName = getDisplayName(user, fallback);
  return displayName.trim().charAt(0).toUpperCase() || fallback;
}

function Navbar({
  cartCount,
  userRole,
  currentUser,
  onLogout,
  onOpenAuthModal,
  theme,
  onToggleTheme,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const navigate = useNavigate();
  const isLoggedIn = Boolean(userRole);
  const isAdmin = userRole?.toLowerCase() === 'admin';
  const logoSrc = theme === 'light' ? '/royalty-logo-white.jpeg' : '/royalty-logo-dark.jpeg';
  const accountInitial = isLoggedIn ? getDisplayInitial(currentUser, isAdmin ? 'A' : 'U') : null;

  const closeMenus = () => {
    setIsExpanded(false);
    setShowAdminMenu(false);
    setShowAccountMenu(false);
  };

  const handleLogout = () => {
    closeMenus();
    onLogout();
    navigate('/home');
  };

  const dropdownLinkClass = 'block px-5 py-3 text-sm text-primary-theme hover:text-accent hover:bg-[rgba(212,175,55,0.08)] transition';
  const dashboardPath = isAdmin ? '/admin' : '/dashboard';

  const accountDropdown = (
    <div className="site-dropdown right-0">
      {isLoggedIn && (
        <Link to={dashboardPath} onClick={closeMenus} className={dropdownLinkClass}>Dashboard</Link>
      )}
      {isLoggedIn && (
        <Link to={isAdmin ? '/admin/bookings' : '/dashboard/wishlist'} onClick={closeMenus} className={dropdownLinkClass}>Wishlist</Link>
      )}
      {!isLoggedIn && (
        <Link to="/auth?mode=signup" onClick={closeMenus} className={dropdownLinkClass}>Signup</Link>
      )}
      <div className="border-t border-luxury" />
      {!isLoggedIn ? (
        <Link to="/auth?mode=login" onClick={closeMenus} className={dropdownLinkClass}>Login</Link>
      ) : (
        <button type="button" onClick={handleLogout} className={`${dropdownLinkClass} w-full text-left`}>Logout</button>
      )}
    </div>
  );

  const adminDropdown = (
    <div className="site-dropdown right-0">
      <Link to="/admin" onClick={closeMenus} className={dropdownLinkClass}>Dashboard</Link>
      <Link to="/admin/bookings" onClick={closeMenus} className={dropdownLinkClass}>Bookings</Link>
      <Link to="/admin/vehicles" onClick={closeMenus} className={dropdownLinkClass}>Vehicles</Link>
      <Link to="/admin/category" onClick={closeMenus} className={dropdownLinkClass}>Categories</Link>
      <Link to="/admin/users" onClick={closeMenus} className={dropdownLinkClass}>Users</Link>
    </div>
  );

  return (
    <nav className="site-navbar fixed left-0 right-0 top-0 z-50">
      <div className="site-navbar-inner premium-navbar-inner flex w-full items-center justify-between px-0 py-0">
        <Link to="/home" onClick={closeMenus} className="flex items-center no-underline">
          <img src={logoSrc} alt="DeRoyalty Car Rental" className="site-navbar-logo h-auto w-[75px]" />
        </Link>

        <button
          type="button"
          onClick={() => setIsExpanded((value) => !value)}
          className="lg:hidden text-primary-theme hover:text-accent"
          aria-label="Toggle navigation"
          aria-expanded={isExpanded}
        >
          <i className={isExpanded ? 'bi bi-x-lg text-2xl' : 'bi bi-list text-3xl'} />
        </button>

        <div className="site-navbar-menu hidden flex-1 items-center lg:flex">
          <div className="site-navbar-primary">
            {isAdmin && (
              <div className="relative">
                <button type="button" onClick={() => setShowAdminMenu((value) => !value)} className={`site-nav-link ${showAdminMenu ? 'active' : ''}`}>
                  ADMIN <i className="bi bi-chevron-down ml-1 text-xs" />
                </button>
                {showAdminMenu && adminDropdown}
              </div>
            )}
            <NavLink to="/home" onClick={closeMenus} className={navClass}>HOME</NavLink>
            <NavLink to="/about" onClick={closeMenus} className={navClass}>ABOUT US</NavLink>
            <NavLink to="/meet-the-team" onClick={closeMenus} className={navClass}>MEET THE TEAM</NavLink>
            <NavLink to="/vehicles" onClick={closeMenus} className={navClass}>VEHICLES</NavLink>
            <NavLink to="/lease-your-vehicle" onClick={closeMenus} className={navClass}>LEASE YOUR VEHICLE</NavLink>
            <NavLink to="/contact" onClick={closeMenus} className="site-nav-cta hidden xl:inline-flex">CONTACT US</NavLink>
          </div>
          <div className="site-navbar-actions">
            <ThemeToggle theme={theme} onToggle={onToggleTheme} />
            {isLoggedIn && (
              <NavLink to="/cart" onClick={closeMenus} className="cart-link relative">
                <i className="bi bi-cart3 text-[25px]" />
                {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
              </NavLink>
            )}
            <div className="site-account-menu">
              <button
                type="button"
                onClick={() => setShowAccountMenu((value) => !value)}
                className={`site-account-button ${showAccountMenu ? 'active' : ''}`}
                aria-label="Open account menu"
                aria-expanded={showAccountMenu}
                title="Account"
              >
                {accountInitial ? (
                  <span className="site-account-initial" aria-hidden="true">{accountInitial}</span>
                ) : (
                  <i className="bi bi-person-circle" aria-hidden="true" />
                )}
                <span className="sr-only">Account</span>
              </button>
              {showAccountMenu && accountDropdown}
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="lg:hidden border-t border-luxury bg-card-theme px-6 py-4">
          <div className="flex flex-col gap-4">
            {isAdmin && (
              <>
                <p className="text-accent font-semibold uppercase">Admin</p>
                <Link to="/admin" onClick={closeMenus} className="site-nav-link">Dashboard</Link>
                <Link to="/admin/bookings" onClick={closeMenus} className="site-nav-link">Bookings</Link>
                <Link to="/admin/vehicles" onClick={closeMenus} className="site-nav-link">Vehicles</Link>
                <Link to="/admin/category" onClick={closeMenus} className="site-nav-link">Categories</Link>
                <Link to="/admin/users" onClick={closeMenus} className="site-nav-link">Users</Link>
              </>
            )}
            <NavLink to="/home" onClick={closeMenus} className={navClass}>HOME</NavLink>
            <NavLink to="/about" onClick={closeMenus} className={navClass}>ABOUT US</NavLink>
            <NavLink to="/meet-the-team" onClick={closeMenus} className={navClass}>MEET THE TEAM</NavLink>
            <NavLink to="/vehicles" onClick={closeMenus} className={navClass}>VEHICLES</NavLink>
            <NavLink to="/lease-your-vehicle" onClick={closeMenus} className={navClass}>LEASE YOUR VEHICLE</NavLink>
            <NavLink to="/contact" onClick={closeMenus} className="btn-luxury-outline w-fit">CONTACT US</NavLink>
            {isLoggedIn && <NavLink to={isAdmin ? '/admin/bookings' : '/dashboard/wishlist'} onClick={closeMenus} className={navClass}>WISHLIST</NavLink>}
            {isLoggedIn && <NavLink to="/cart" onClick={closeMenus} className={navClass}>CART ({cartCount})</NavLink>}
            {isLoggedIn && <NavLink to={dashboardPath} onClick={closeMenus} className={navClass}>DASHBOARD</NavLink>}
            <div className="flex items-center justify-between border-t border-luxury pt-4">
              <ThemeToggle theme={theme} onToggle={onToggleTheme} />
              {!isLoggedIn ? (
                <div className="flex gap-3">
                  <Link to="/auth?mode=signup" onClick={closeMenus} className="btn-luxury-outline">Signup</Link>
                  <Link to="/auth?mode=login" onClick={closeMenus} className="btn-luxury">Login</Link>
                </div>
              ) : (
                <button type="button" onClick={handleLogout} className="btn-luxury-outline">Logout</button>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
